import {FieldArray, Form, FormikProvider, useFormik} from "formik"
import * as Yup from "yup"
import Divider from "@mui/material/Divider"
import React, {useEffect} from "react"
import Grid from "@mui/material/Grid"
import Stack from "@mui/material/Stack"
import InputLabel from "@mui/material/InputLabel"
import TextField from "@mui/material/TextField"
import Button from "@mui/material/Button"
import DialogTitle from "@mui/material/DialogTitle"
import {
    Autocomplete,
    CardContent,
    Checkbox,
    DialogActions,
    DialogContent,
    FormControlLabel,
    FormHelperText,
    IconButton,
    MenuItem,
    Select
} from "@mui/material"
import {useAppDispatch, useAppSelector} from "reducers/hooks"
import {DogElement, saveElement, updateElement} from "reducers/Dog/DogReducer"
import FormControl from "@mui/material/FormControl"
import {getAllElement} from "../../../reducers/Municipality/MunicipalityReducer"
import {getAllElementBoxNotPageable} from "../../../reducers/Box/BoxReducer"
import {Add, BoxRemove, DocumentDownload} from "iconsax-react"
import Card from "@mui/material/Card"
import CardMedia from "@mui/material/CardMedia"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import {FormattedMessage, useIntl} from "react-intl"
import {DatePicker} from "@mui/x-date-pickers/DatePicker"
import Dropzone from "react-dropzone"
import dayjs from "dayjs"
import {createFilterOptions} from "@mui/material/Autocomplete"
import {ColumnFiltersState} from "@tanstack/react-table"

interface DogElementFormProps {
    closeModal: () => void
    element: DogElement | null
    onSuccess?: () => void
    pageableData?: {
        page: number
        rowsPerPage: number
        filters: ColumnFiltersState | null
    }
}

export const DogElementForm: React.FC<DogElementFormProps> = ({
                                                                  closeModal,
                                                                  element,
                                                                  onSuccess,
                                                                  pageableData
                                                              }) => {
    const cities = useAppSelector((state) => state.municipalityElement.elements)
    const boxes = useAppSelector((state) => state.boxElement.elements || [])
    const intl = useIntl()


    const dispatch = useAppDispatch()

    useEffect(() => {
        dispatch(getAllElement())
        dispatch(getAllElementBoxNotPageable())

    }, [])

    const formik = useFormik({
        initialValues: element
            ? {
                _id: element ? element._id : '',
                name: element ? element.name : '',
                microchipCode: element ? element.microchipCode : '',
                municipalityId: element ? element.municipalityId : '',
                sterilizationDate: element && element.sterilizationDate ? dayjs(element.sterilizationDate, "DD/MM/YYYY") : null,
                sex: element ? element.sex : '',
                catchingHealthStatus: element ? element.catchingHealthStatus : '',
                coatColor: element ? element.coatColor : '',
                dogStatusType: element ? element.dogStatusType : '',
                race: element ? element.race : '',
                isSterilizied: element ? element.isSterilizied : false,
                catchingDate: element ? dayjs(element.catchingDate, "DD/MM/YYYY") : null,
                photoProfil: element ? element.photoProfil : '',
                bornDate: element && element.bornDate ? dayjs(element.bornDate, "DD/MM/YYYY") : null,
                diedDate: element && element.diedDate ? dayjs(element.diedDate, "DD/MM/YYYY") : null,
                returnedDate: element && element.returnedDate ? dayjs(element.returnedDate, "DD/MM/YYYY") : null,
                adoptionDate: element && element.adoptionDate ? dayjs(element.adoptionDate, "DD/MM/YYYY") : null,
                sizeDog: element ? element.sizeDog : null,
                dogHairLength: element ? element.dogHairLength : '',
                box: element?.box ? {
                    _id: element.box._id,
                    name: element.box.name, 
                    shedName: element.box.shedName
                } : null,
                multimedias: element?.multimedias && element.multimedias.length > 0
                    ? [...element.multimedias]
                    : [],
                treatments: element.treatments.map(treatment => ({
                    _id: treatment._id || '',
                    notes: treatment.notes || '',
                    executionDate: dayjs(treatment.executionDate, "DD/MM/YYYY"),
                    multimedias: treatment.multimedias || [],
                }))
            } :
            {
                _id: '',
                name: '',
                microchipCode: '',
                municipalityId: '',
                sex: '',
                catchingDate: null,
                dogStatusType: 'CATCHED',
                catchingHealthStatus: '',
                coatColor: '',
                race: '',
                dogHairLength: '',
                isSterilizied: false,
                sterilizationDate: null,
                photoProfil: '',
                multimedias: [],
                box: null,
                treatments: [],
                bornDate: null,
                diedDate: null,
                returnedDate: null,
                adoptionDate: null,
                sizeDog: null,
            },
        validationSchema: Yup.object().shape({
            name: Yup.string()
                .min(2, "Il nome deve essere lungo almeno 2 caratteri")
                .max(100, "Il nome non può superare i 100 caratteri")
                .optional(),
            microchipCode: Yup.string()
                .min(15, "L'emento deve essere lungo 15 caratteri")
                .max(15, "L'emento deve essere lungo 15 caratteri")
                .matches(/^[0-9]{15}$/, "Il codice microchip deve essere composto da 15 cifre")
                .required("Il codice microchip è obbligatorio"),
            sex: Yup.string()
                .oneOf(['M', 'F'], "Il sesso deve essere 'M' o 'F'")
                .required("Il sesso è obbligatorio"),
            municipalityId: Yup.string().required("Il comune è obbligatorio"),
            coatColor: Yup.string().optional(),
            race: Yup.string().optional(),
            isSterilizied: Yup.boolean(),
            dogHairLength: Yup.string().required("Questo campo è obbligatorio"),
            dogStatusType: Yup.string().optional(),
            photoProfil: Yup.mixed().optional(),
            catchingDate: Yup.object().required("La data di cattura è obbligatoria!"),
            catchingHealthStatus: Yup.string().required("Lo stato di salute è obbligatorio!"),
            treatments: Yup.array().of(Yup.object().shape({
                notes: Yup.string().required("La stringa è obbligatoria"),
                executionDate: Yup.object().required("The Execution Date is required"),
                multimedias: Yup.array().of(
                    Yup.object().shape({
                        name: Yup.string(),
                        base64EncodedContent: Yup.string().required("The Base64 is required"),
                    }).optional()
                ),
            })),
            multimedias: Yup.array().of(
                Yup.object().shape({
                    name: Yup.string(),
                    base64EncodedContent: Yup.string().required("The Base64 is required"),
                }).optional()
            ),
            sizeDog: Yup.string().required("La dimensione del cane è obbligatoria!"),
            sterilizationDate: Yup.object().when("isSterilizied", {
                is: (val: boolean) => {
                    return val
                },
                then: (s) => s.required(),
                otherwise: (s) => s.nullable(),
            }),
            bornDate: Yup.object().nullable(),
            diedDate: Yup.object().nullable(),
            returnedDate: Yup.object().nullable(),
            adoptionDate: Yup.object().nullable()
        }),
        enableReinitialize: true,
        onSubmit: async (values) => {

            const preparedData: DogElement = {
                ...values,
                catchingDate: values.catchingDate && values.catchingDate.format(),
                bornDate: values.bornDate && values.bornDate.format(),
                diedDate: values.diedDate && values.diedDate.format(),
                sterilizationDate: values.sterilizationDate && values.sterilizationDate.format(),
                adoptionDate: values.adoptionDate && values.adoptionDate.format(),
                returnedDate: values.returnedDate && values.returnedDate.format(),

                box: values.box
                    ? {
                        _id: values.box._id,
                        name: values.box.name,
                        shedName: values.box.shedName
                    }
                    : null,
                treatments: values.treatments.length !== 0 ? values.treatments.map(treatment => {
                    return {
                        _id: treatment._id,
                        notes: treatment.notes,
                        executionDate: treatment.executionDate.format(),
                        multimedias: treatment.multimedias
                    }
                }) : []
            }

            try {
                if (element && element._id) {
                    dispatch(updateElement({
                        data: preparedData,
                        callback: () => {
                            closeModal()
                            onSuccess?.()
                        }
                    }))
                } else {
                    dispatch(saveElement({
                        data: preparedData,
                        callback: () => {
                            closeModal()
                            onSuccess?.()
                        }
                    }))
                }
                closeModal()
            } catch (error) {
                console.error('Error:', error)
            }
        }
    })

    /*    useEffect(() => {
            console.log("element", element)
        }, [])*/

    // custom filter
    const filterOptions = createFilterOptions({
        matchFrom: 'start', // Cerca solo dall'inizio della stringa
        ignoreCase: true,   // Ignora maiuscole/minuscole
    })

    const selectedBox = boxes.find(
        (box) => box._id === formik.values.box?._id
    )
    const selectedCity = cities.find(city => city.name === formik.values.municipalityId) || null

    return (
        <>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                    <DialogTitle>{intl.formatMessage({id: "New Dog Entry"})}</DialogTitle>
                    <Divider/>
                    <DialogContent sx={{
                        p: 2.5,
                        borderRadius: 2
                    }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="name">
                                        <FormattedMessage id="Notes/Name/Place of Capture/Adopter name"
                                                          defaultMessage="Notes/Name/Place of Capture/Adopter name"/>
                                    </InputLabel>
                                    <TextField
                                        fullWidth
                                        id="name"
                                        placeholder={intl.formatMessage({id: "Notes/Name/Place of Capture/Adopter name"})}
                                        {...formik.getFieldProps('name')}
                                        error={Boolean(formik.touched.name && formik.errors.name)}
                                        helperText={formik.touched.name && formik.errors.name}
                                    />
                                </Stack>
                            </Grid>

                            {/* SECTION ADOPTION  DATE*/}
                            <Grid item xs={12} sm={4}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="adoptionDate">
                                        <FormattedMessage id="Date Adoption" defaultMessage="Date Adoption"/>
                                    </InputLabel>

                                    <DatePicker
                                        label={<FormattedMessage id="Enter in case of Adoption"
                                                                 defaultMessage="Enter in case of Adoption"/>}
                                        value={formik.values.adoptionDate}
                                        onChange={(newDate) => {
                                            if (newDate) {
                                                formik.setFieldValue('adoptionDate', newDate)
                                            }
                                        }}
                                        format={"DD/MM/YYYY"}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: Boolean(formik.touched.adoptionDate && formik.errors.adoptionDate),
                                                helperText: formik.touched.adoptionDate && formik.errors.adoptionDate ? formik.errors.adoptionDate : '',
                                            },
                                        }}
                                    />
                                </Stack>
                            </Grid>

                            {/* SECTION DOG STATUS TYPE*/}
                            <Grid item xs={12} sm={2}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="dogStatusType">
                                        <FormattedMessage id="Dog Status Type" defaultMessage="dogStatusType"/>
                                    </InputLabel>
                                    <FormControl fullWidth error={Boolean(formik.touched.sex && formik.errors.sex)}>
                                        <Select
                                            id="dogStatusType"
                                            {...formik.getFieldProps('dogStatusType')}
                                            value={formik.values.dogStatusType}
                                            onChange={(e) => formik.setFieldValue('dogStatusType', e.target.value)}
                                        >
                                            <MenuItem value="CATCHED">
                                                <FormattedMessage id="Catched" defaultMessage="Catched"/>
                                            </MenuItem>
                                            <MenuItem value="LONG_PERMANATION">
                                                <FormattedMessage id="Long Permanation"
                                                                  defaultMessage="Long Permanation"/>
                                            </MenuItem>

                                            <MenuItem value="ADOPTED">
                                                <FormattedMessage id="Adopted" defaultMessage="Adopted"/>
                                            </MenuItem>

                                            <MenuItem value="DIED">
                                                <FormattedMessage id="Died" defaultMessage="Died"/>
                                            </MenuItem>

                                            <MenuItem value="RETURNED">
                                                <FormattedMessage id="Returned" defaultMessage="Returned"/>
                                            </MenuItem>

                                        </Select>
                                        {formik.touched.dogStatusType && formik.errors.dogStatusType && (
                                            <FormHelperText>{formik.errors.dogStatusType}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Stack>
                            </Grid>
                            {/* SECTION Microchip */}
                            <Grid item xs={12} sm={6}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="microchipCode">
                                        <FormattedMessage id="MicrochipCode" defaultMessage="MicrochipCode"/>
                                    </InputLabel>
                                    <TextField
                                        fullWidth
                                        id="microchipCode"
                                        placeholder={intl.formatMessage({id: "MicrochipCode"})}
                                        {...formik.getFieldProps('microchipCode')}
                                        error={Boolean(formik.touched.microchipCode && formik.errors.microchipCode)}
                                        helperText={formik.touched.microchipCode && formik.errors.microchipCode}
                                    />
                                </Stack>
                            </Grid>

                            {/* SECTION EXECUTION DATE*/}
                            <Grid item xs={12} sm={6}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="catchingDate">
                                        <FormattedMessage id="Catching Date" defaultMessage="Catching Date"/>
                                    </InputLabel>

                                    <DatePicker
                                        label={<FormattedMessage id="Capture/Arrival Date"
                                                                 defaultMessage="Capture/Arrival Date"/>}
                                        value={formik.values.catchingDate}
                                        onChange={(newDate) => {
                                            if (newDate) {
                                                formik.setFieldValue('catchingDate', newDate)
                                            }
                                        }}
                                        format={"DD/MM/YYYY"}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: Boolean(formik.touched.catchingDate && formik.errors.catchingDate),
                                                helperText: formik.touched.catchingDate && formik.errors.catchingDate ? formik.errors.catchingDate : '',
                                            },
                                        }}
                                    />
                                </Stack>
                            </Grid>

                            {/* SECTION CITY */}
                            <Grid item xs={12} sm={4}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="municipalityId">
                                        <FormattedMessage id="city" defaultMessage="city"/>
                                    </InputLabel>
                                    <Autocomplete
                                        id="municipalityId"
                                        value={selectedCity || undefined}
                                        onChange={(e, newValue) => {
                                            formik.setFieldValue("municipalityId", newValue ? newValue.name : '')
                                        }}
                                        options={cities}
                                        getOptionLabel={(option) => option.name || option.name || ''}
                                        isOptionEqualToValue={(option, value) => value ? option.name === value.name : false}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={<FormattedMessage id="select City"
                                                                         defaultMessage="select City"/>}
                                                error={Boolean(formik.touched.municipalityId && formik.errors.municipalityId)}
                                                helperText={formik.touched.municipalityId && formik.errors.municipalityId}
                                            />
                                        )}
                                        disableClearable
                                        noOptionsText="No city found"
                                    />
                                </Stack>
                            </Grid>

                            {/* SECTION SEX */}
                            <Grid item xs={12} sm={4}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="sex">
                                        <FormattedMessage id="Sex" defaultMessage="Sex"/>
                                    </InputLabel>
                                    <FormControl fullWidth error={Boolean(formik.touched.sex && formik.errors.sex)}>
                                        <Select
                                            id="sex"
                                            {...formik.getFieldProps('sex')}
                                            value={formik.values.sex}
                                            onChange={(e) => formik.setFieldValue('sex', e.target.value)}
                                        >
                                            <MenuItem value="M">
                                                <FormattedMessage id="Male" defaultMessage="Male"/>
                                            </MenuItem>
                                            <MenuItem value="F">
                                                <FormattedMessage id="Female" defaultMessage="Female"/>
                                            </MenuItem>
                                        </Select>
                                        {formik.touched.sex && formik.errors.sex && (
                                            <FormHelperText>{formik.errors.sex}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Stack>
                            </Grid>

                            {/* SECTION SIZE DOG */}
                            <Grid item xs={12} sm={4}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="sizeDog">
                                        <FormattedMessage id="sizeDog" defaultMessage="Size Dog"/>
                                    </InputLabel>
                                    <FormControl fullWidth
                                                 error={Boolean(formik.touched.sizeDog && formik.errors.sizeDog)}>
                                        <Select
                                            id="sizeDog"
                                            {...formik.getFieldProps('sizeDog')}
                                            value={formik.values.sizeDog}
                                            onChange={(e) => formik.setFieldValue('sizeDog', e.target.value)}
                                        >
                                            <MenuItem value="P">
                                                <FormattedMessage id="Small" defaultMessage="Small"/>
                                            </MenuItem>
                                            <MenuItem value="M">
                                                <FormattedMessage id="Medium" defaultMessage="Medium"/>
                                            </MenuItem>
                                            <MenuItem value="G">
                                                <FormattedMessage id="Large" defaultMessage="Large"/>
                                            </MenuItem>
                                            <MenuItem value="MG">
                                                <FormattedMessage id="Extra Large" defaultMessage="Extra Large"/>
                                            </MenuItem>
                                        </Select>
                                        {formik.touched.sizeDog && formik.errors.sizeDog && (
                                            <FormHelperText>{formik.errors.sizeDog}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Stack>
                            </Grid>

                            {/* SECTION FOTO PROFILO*/}
                            <Grid item xs={12} sm={8}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="photoProfil">
                                        <FormattedMessage id="Profile Photo" defaultMessage="Profile Photo" />
                                    </InputLabel>
                                    <TextField
                                        fullWidth
                                        id="photoProfil"
                                        name="photoProfil"
                                        type="file"
                                        inputProps={{ accept: "image/*" }}
                                        onChange={(event) => {
                                            const target = event.currentTarget as HTMLInputElement;
                                            const file = target.files && target.files[0];
                                            if (file) {
                                                const MAX_FILE_SIZE = 1 * 1024 * 1024
                                                if (file.size > MAX_FILE_SIZE) {
                                                    alert(`Il file ${file.name} supera 1MB e non verrà caricato.`);
                                                    return;
                                                }
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    formik.setFieldValue("photoProfil", reader.result);
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                        error={Boolean(formik.touched.photoProfil && formik.errors.photoProfil)}
                                        helperText={formik.touched.photoProfil && formik.errors.photoProfil}
                                    />

                                    {/* Display the card with the image preview */}
                                    {formik.values.photoProfil && (
                                        <Card sx={{ maxWidth: 345, marginTop: 2 }}>
                                            <CardContent>
                                                <img
                                                    src={formik.values.photoProfil}
                                                    alt="Profile Preview"
                                                    style={{
                                                        width: '100%',
                                                        height: 'auto',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px',
                                                        border: '1px solid #ccc',
                                                    }}
                                                />
                                                {/* Button to remove the image */}
                                                <Button
                                                    variant="text"
                                                    color="error"
                                                    sx={{ marginTop: 1 }}
                                                    onClick={() => formik.setFieldValue("photoProfil", "")} // Clear the field
                                                >
                                                    <FormattedMessage id="Remove Photo" defaultMessage="Remove Photo" />
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    )}
                                </Stack>
                            </Grid>

                            {/* SECTION BORN DATE */}
                            <Grid item xs={12} sm={4}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="bornDate">
                                        <FormattedMessage id="bornDate" defaultMessage="bornDate"/>
                                    </InputLabel>

                                    <DatePicker

                                        value={formik.values.bornDate}
                                        onChange={(newDate) => {
                                            if (newDate) {
                                                formik.setFieldValue('bornDate', newDate)
                                            }
                                        }}
                                        format={"DD/MM/YYYY"}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: Boolean(formik.touched.bornDate && formik.errors.bornDate),
                                                helperText: formik.touched.bornDate && formik.errors.bornDate ? formik.errors.bornDate : '',
                                            },
                                        }}
                                    />
                                </Stack>

                            </Grid>

                            {/* SECTION COAT COLOR*/}
                            <Grid item xs={12} sm={4}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="coatColor">
                                        <FormattedMessage id="coatColor" defaultMessage="coatColor"/>
                                    </InputLabel>
                                    <TextField
                                        fullWidth
                                        id="coatColor"
                                        placeholder={intl.formatMessage({id: "coatColor"})}
                                        {...formik.getFieldProps('coatColor')}
                                        error={Boolean(formik.touched.coatColor && formik.errors.coatColor)}
                                        helperText={formik.touched.coatColor && formik.errors.coatColor}
                                    />
                                </Stack>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="dogHairLength">
                                        <FormattedMessage id="Dog Hair Length" defaultMessage="Dog Hair Length"/>
                                    </InputLabel>
                                    <FormControl fullWidth
                                                 error={Boolean(formik.touched.dogHairLength && formik.errors.dogHairLength)}>
                                        <Select
                                            id="sex"
                                            {...formik.getFieldProps('dogHairLength')}
                                            value={formik.values.dogHairLength}
                                            onChange={(e) => formik.setFieldValue('dogHairLength', e.target.value)}
                                        >
                                            <MenuItem value="SHORT">
                                                <FormattedMessage id="Short" defaultMessage="Male"/>
                                            </MenuItem>
                                            <MenuItem value="MEDIUM">
                                                <FormattedMessage id="Medium" defaultMessage="Medium"/>
                                            </MenuItem>

                                            <MenuItem value="LONG">
                                                <FormattedMessage id="Long" defaultMessage="Long"/>
                                            </MenuItem>

                                        </Select>
                                        {formik.touched.dogHairLength && formik.errors.dogHairLength && (
                                            <FormHelperText>{formik.errors.dogHairLength}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Stack>
                            </Grid>


                            {/* SECTION catchingHealthStatus */}
                            <Grid item xs={12} sm={4}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="catchingHealthStatus">
                                        <FormattedMessage id="Health Status" defaultMessage="Health Status"/>
                                    </InputLabel>
                                    <FormControl fullWidth
                                                 error={Boolean(formik.touched.catchingHealthStatus && formik.errors.catchingHealthStatus)}>
                                        <InputLabel htmlFor="catchingHealthStatus">
                                            <FormattedMessage id="Health Status" defaultMessage="Health Status"/>
                                        </InputLabel>
                                        <Select
                                            id="catchingHealthStatus"
                                            {...formik.getFieldProps('catchingHealthStatus')}
                                            value={formik.values.catchingHealthStatus}
                                            onChange={(e) => formik.setFieldValue('catchingHealthStatus', e.target.value)}
                                        >
                                            <MenuItem value="HEALTHY">
                                                <FormattedMessage id="HEALTHY" defaultMessage="HEALTHY"/>
                                            </MenuItem>
                                            <MenuItem value="SICK">
                                                <FormattedMessage id="SICK" defaultMessage="SICK"/>
                                            </MenuItem>

                                            <MenuItem value="RECOVERING">
                                                <FormattedMessage id="RECOVERING" defaultMessage="RECOVERING"/>
                                            </MenuItem>
                                            <MenuItem value="INJURED">
                                                <FormattedMessage id="INJURED" defaultMessage="INJURED"/>
                                            </MenuItem>
                                            <MenuItem value="UNDER_TREATMENT">
                                                <FormattedMessage id="UNDER TREATMENT"
                                                                  defaultMessage="UNDER TREATMENT"/>
                                            </MenuItem>
                                            <MenuItem value="CRITICAL">
                                                <FormattedMessage id="CRITICAL" defaultMessage="CRITICAL"/>
                                            </MenuItem>
                                            <MenuItem value="STABLE">
                                                <FormattedMessage id="STABLE" defaultMessage="STABLE"/>
                                            </MenuItem>
                                            <MenuItem value="WEAK">
                                                <FormattedMessage id="WEAK" defaultMessage="WEAK"/>
                                            </MenuItem>
                                            <MenuItem value="MALNOURISHED">
                                                <FormattedMessage id="MALNOURISHED" defaultMessage="MALNOURISHED"/>
                                            </MenuItem>
                                            <MenuItem value="INFECTED">
                                                <FormattedMessage id="INFECTED" defaultMessage="INFECTED"/>
                                            </MenuItem>
                                            <MenuItem value="FRAIL">
                                                <FormattedMessage id="FRAIL" defaultMessage="FRAIL"/>
                                            </MenuItem>
                                            <MenuItem value="PARALYZED">
                                                <FormattedMessage id="PARALYZED" defaultMessage="PARALYZED"/>
                                            </MenuItem>
                                            <MenuItem value="DEHYDRATED">
                                                <FormattedMessage id="DEHYDRATED" defaultMessage="DEHYDRATED"/>
                                            </MenuItem>
                                            <MenuItem value="RECOVERED">
                                                <FormattedMessage id="RECOVERED" defaultMessage="RECOVERED"/>
                                            </MenuItem>
                                        </Select>
                                        {formik.touched.catchingHealthStatus && formik.errors.catchingHealthStatus && (
                                            <FormHelperText>{formik.errors.catchingHealthStatus}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Stack>
                            </Grid>

                            {/* Files Section */}
                            <Grid item xs={12}>
                                <InputLabel htmlFor="multimedias">
                                    <FormattedMessage id="Add multimedia" defaultMessage="Add multimedia"/>
                                </InputLabel>
                                <FieldArray name="multimedias">
                                    {({ push, remove }) => (
                                        <Grid container spacing={2}>
                                            {formik.values.multimedias.map((file, index) => (
                                                <Grid item xs={12} sm={6} md={4} key={index}>
                                                    <Card variant="outlined" sx={{ position: "relative" }}>
                                                        <IconButton
                                                            color="error"
                                                            onClick={() => remove(index)}
                                                            size="small"
                                                            sx={{
                                                                position: "absolute",
                                                                top: 8,
                                                                right: 8,
                                                                zIndex: 2,
                                                                backgroundColor: "rgba(255, 255, 255, 0.8)",
                                                                boxShadow: 1,
                                                                '&:hover': {
                                                                    backgroundColor: "rgba(255, 0, 0, 0.8)",
                                                                    color: "white",
                                                                },
                                                            }}
                                                        >
                                                            <BoxRemove />
                                                        </IconButton>

                                                        {file.base64EncodedContent && (
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => {
                                                                    const link = document.createElement("a")
                                                                    link.href = file.base64EncodedContent
                                                                    link.download = file.name
                                                                    link.click()
                                                                }}
                                                                sx={{
                                                                    position: "absolute",
                                                                    bottom: 8,
                                                                    right: 8,
                                                                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                                                                    boxShadow: 1,
                                                                    '&:hover': {
                                                                        backgroundColor: "rgba(25, 118, 210, 0.8)",
                                                                        color: "white",
                                                                    },
                                                                }}
                                                            >
                                                                <DocumentDownload />
                                                            </IconButton>
                                                        )}

                                                        {file.base64EncodedContent ? (
                                                            <>
                                                                {file.type.startsWith("PNG") || file.type.startsWith("JPG") || file.type.startsWith("JPEG") ? (
                                                                    <CardMedia
                                                                        component="img"
                                                                        height="140"
                                                                        src={file.base64EncodedContent}
                                                                        alt={file.name || "Uploaded Image"}
                                                                        sx={{ objectFit: "cover" }}
                                                                    />
                                                                ) : file.type === "PDF" ? (
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            height: "140px",
                                                                        }}
                                                                    >
                                                                        <iframe
                                                                            src={file.base64EncodedContent}
                                                                            title={file.name || "Uploaded PDF"}
                                                                            style={{
                                                                                width: "100%",
                                                                                height: "100%",
                                                                                border: "none",
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                ) : file.type === "application/msword" ||
                                                                file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ? (
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            height: "140px",
                                                                        }}
                                                                    >
                                                                        <Typography variant="caption" color="textSecondary" noWrap>
                                                                            {file.name || "DOC/DOCX File"}
                                                                        </Typography>
                                                                    </Box>
                                                                ) : (
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            height: "140px",
                                                                        }}
                                                                    >
                                                                        <Typography variant="caption" color="textSecondary" noWrap>
                                                                            {file.name}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    height: "140px",
                                                                }}
                                                            >
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {file.name}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Card>
                                                </Grid>
                                            ))}

                                            <Grid item xs={12} sm={6} md={4}>
                                                <Dropzone
                                                    onDrop={(acceptedFiles) => {
                                                        const MAX_FILE_SIZE = 1024 * 1024; // 1MB
                                                        acceptedFiles.forEach((file) => {
                                                            if (file.size > MAX_FILE_SIZE) {
                                                                alert(`Il file ${file.name} supera i 1MB e non verrà caricato.`);
                                                                return;
                                                            }
                                                            const reader = new FileReader();
                                                            reader.onloadend = () => {
                                                                push({
                                                                    name: file.name,
                                                                    base64EncodedContent: reader.result,
                                                                    type: file.type,
                                                                });
                                                            };
                                                            reader.readAsDataURL(file);
                                                        });
                                                    }}
                                                    accept={{
                                                        "image/*": [".png", ".jpeg", ".jpg"],
                                                        "application/pdf": [".pdf"],
                                                        "application/msword": [".doc", ".docx"],
                                                    }}
                                                    multiple
                                                >
                                                    {({ getRootProps, getInputProps }) => (
                                                        <Card
                                                            variant="outlined"
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                height: "140px",
                                                                cursor: "pointer",
                                                            }}
                                                            {...getRootProps()}
                                                        >
                                                            <input {...getInputProps()} />
                                                            <Add fontSize="large" />
                                                            <Typography variant="body2" color="textSecondary">
                                                                <FormattedMessage
                                                                    id="Drag and drop files here, or click to upload"
                                                                    defaultMessage="Drag and drop files here, or click to upload"
                                                                />
                                                            </Typography>
                                                        </Card>
                                                    )}
                                                </Dropzone>
                                            </Grid>
                                        </Grid>
                                    )}
                                </FieldArray>
                            </Grid>

                            { /* SECTION TRATTAMENTO*/}
                            <Grid item xs={12}>
                                <FieldArray name="treatments">
                                    {({ push, remove }) => (
                                        <Stack spacing={3}>
                                            <Typography variant="h6">
                                                <FormattedMessage id="Treatment" defaultMessage="Treatment" />
                                            </Typography>

                                            {(formik.values.treatments || []).map((treatment, index) => (
                                                <Stack key={index} spacing={1} alignItems="flex-start">
                                                    <TextField
                                                        fullWidth
                                                        label={<FormattedMessage id="Notes" defaultMessage="Notes" />}
                                                        name={`treatments[${index}].notes`}
                                                        value={treatment.notes || ''}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        error={Boolean(
                                                            formik.touched.treatments?.[index]?.notes &&
                                                            formik.errors.treatments?.[index]
                                                        )}
                                                        helperText={formik.touched.treatments?.[index]?.notes}
                                                    />

                                                    <DatePicker
                                                        label={
                                                            <FormattedMessage
                                                                id="Execution Date"
                                                                defaultMessage="Execution Date"
                                                            />
                                                        }
                                                        value={treatment.executionDate}
                                                        onChange={(newDate) => {
                                                            formik.setFieldValue(
                                                                `treatments[${index}].executionDate`,
                                                                newDate
                                                            )
                                                        }}
                                                        format={"DD/MM/YYYY"}
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                error: Boolean(
                                                                    formik.touched.treatments?.[index]?.executionDate &&
                                                                    formik.errors.treatments?.[index]
                                                                ),
                                                                helperText:
                                                                    formik.errors.treatments?.[index] &&
                                                                    'La data di trattamento è obbligatoria!'
                                                            },
                                                        }}
                                                    />

                                                    {/* Updated Files Section */}
                                                    <InputLabel htmlFor={`treatments[${index}].multimedias`}>
                                                        <FormattedMessage
                                                            id="Add treatment files"
                                                            defaultMessage="Add treatment files"
                                                        />
                                                    </InputLabel>
                                                    <Grid container spacing={2}>
                                                        {(treatment.multimedias || []).map((file, fileIndex) => (
                                                            <Grid item xs={12} sm={6} md={4} key={fileIndex}>
                                                                <Card variant="outlined" sx={{ position: "relative" }}>
                                                                    <IconButton
                                                                        color="error"
                                                                        onClick={() => {
                                                                            const newMultimedias = treatment.multimedias.filter(
                                                                                (_, i) => i !== fileIndex
                                                                            )
                                                                            formik.setFieldValue(
                                                                                `treatments[${index}].multimedias`,
                                                                                newMultimedias
                                                                            )
                                                                        }}
                                                                        size="small"
                                                                        sx={{
                                                                            position: "absolute",
                                                                            top: 8,
                                                                            right: 8,
                                                                            zIndex: 2,
                                                                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                                                                            boxShadow: 1,
                                                                            '&:hover': {
                                                                                backgroundColor: "rgba(255, 0, 0, 0.8)",
                                                                                color: "white",
                                                                            },
                                                                        }}
                                                                    >
                                                                        <BoxRemove />
                                                                    </IconButton>

                                                                    {file.base64EncodedContent ? (
                                                                        <>
                                                                            {file.type.startsWith("PNG") ||
                                                                            file.type.startsWith("JPG") ||
                                                                            file.type.startsWith("JPEG") ? (
                                                                                <CardMedia
                                                                                    component="img"
                                                                                    height="140"
                                                                                    src={file.base64EncodedContent}
                                                                                    alt={file.name || "Uploaded Image"}
                                                                                    sx={{ objectFit: "cover" }}
                                                                                />
                                                                            ) : file.type === "PDF" ? (
                                                                                <Box
                                                                                    sx={{
                                                                                        display: "flex",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "center",
                                                                                        height: "140px",
                                                                                    }}
                                                                                >
                                                                                    <iframe
                                                                                        src={file.base64EncodedContent}
                                                                                        title={file.name || "Uploaded PDF"}
                                                                                        style={{
                                                                                            width: "100%",
                                                                                            height: "100%",
                                                                                            border: "none",
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                            ) : (
                                                                                <Box
                                                                                    sx={{
                                                                                        display: "flex",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "center",
                                                                                        height: "140px",
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        color="textSecondary"
                                                                                        noWrap
                                                                                    >
                                                                                        {file.name}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <Box
                                                                            sx={{
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                height: "140px",
                                                                            }}
                                                                        >
                                                                            <Typography variant="caption" color="textSecondary">
                                                                                {file.name}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}

                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() => {
                                                                            const link = document.createElement("a")
                                                                            link.href = file.base64EncodedContent
                                                                            link.download = file.name
                                                                            link.click()
                                                                        }}
                                                                        sx={{
                                                                            position: "absolute",
                                                                            bottom: 8,
                                                                            right: 8,
                                                                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                                                                            boxShadow: 1,
                                                                        }}
                                                                    >
                                                                        <DocumentDownload />
                                                                    </IconButton>
                                                                </Card>
                                                            </Grid>
                                                        ))}

                                                        <Grid item xs={12} sm={6} md={4}>
                                                            <Dropzone
                                                                onDrop={(acceptedFiles) => {
                                                                    const MAX_FILE_SIZE = 2 * 1024 * 1024 // 5MB
                                                                    acceptedFiles.forEach((file) => {
                                                                        if (file.size > MAX_FILE_SIZE) {
                                                                            alert(`Il file ${file.name} supera i 2MB e non verrà caricato.`)
                                                                            return
                                                                        }
                                                                        const reader = new FileReader()
                                                                        reader.onloadend = () => {
                                                                            const newMultimedia = {
                                                                                name: file.name,
                                                                                base64EncodedContent: reader.result,
                                                                                type: file.type,
                                                                            }
                                                                            const currentMultimedias =
                                                                                formik.values.treatments[index].multimedias || []
                                                                            formik.setFieldValue(
                                                                                `treatments[${index}].multimedias`,
                                                                                [...currentMultimedias, newMultimedia]
                                                                            )
                                                                        }
                                                                        reader.readAsDataURL(file)
                                                                    })
                                                                }}
                                                                accept={{
                                                                    "image/*": [".png", ".jpeg", ".jpg"],
                                                                    "application/pdf": [".pdf"],
                                                                    "application/msword": [".doc", ".docx"],
                                                                }}
                                                                multiple
                                                            >
                                                                {({ getRootProps, getInputProps }) => (
                                                                    <Card
                                                                        variant="outlined"
                                                                        sx={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            height: "140px",
                                                                            cursor: "pointer",
                                                                        }}
                                                                        {...getRootProps()}
                                                                    >
                                                                        <input {...getInputProps()} />
                                                                        <Stack alignItems="center" spacing={1}>
                                                                            <Add fontSize="large" />
                                                                            <Typography variant="body2" color="textSecondary">
                                                                                <FormattedMessage
                                                                                    id="Drag and drop files here, or click to upload"
                                                                                    defaultMessage="Drag and drop files here, or click to upload"
                                                                                />
                                                                            </Typography>
                                                                        </Stack>
                                                                    </Card>
                                                                )}
                                                            </Dropzone>
                                                        </Grid>
                                                    </Grid>

                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={<BoxRemove />}
                                                        onClick={() => remove(index)}
                                                        sx={{ mt: 2 }}
                                                    >
                                                        <FormattedMessage id="RemoveTreatment" defaultMessage="Remove Treatment" />
                                                    </Button>
                                                </Stack>
                                            ))}

                                            <Button
                                                variant="outlined"
                                                startIcon={<Add />}
                                                onClick={() =>
                                                    push({
                                                        notes: '',
                                                        executionDate: '',
                                                        multimedias: [],
                                                    })
                                                }
                                            >
                                                <FormattedMessage id="Add Treatment" defaultMessage="Add Treatment" />
                                            </Button>
                                        </Stack>
                                    )}
                                </FieldArray>
                            </Grid>
                            {/* SECTION BOX */}
                            <Grid item xs={12} sm={6}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="box">
                                        <FormattedMessage id="Box" defaultMessage="Box"/>
                                    </InputLabel>
                                    <Autocomplete<any>
                                        id="box"
                                        value={formik.values.box || undefined}
                                        onChange={(e, newValue) => {
                                            formik.setFieldValue("box", newValue ? newValue : '')
                                        }}
                                        options={boxes}
                                        getOptionLabel={(option) => {
                                            return `${option.name} ${option.shedName}`
                                        }}
                                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                        filterOptions={filterOptions}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={<FormattedMessage id="Select Box" defaultMessage="Select Box"/>}
                                                error={Boolean(formik.touched.box && formik.errors.box)}
                                            />
                                        )}
                                        noOptionsText="No boxes found"
                                    />
                                </Stack>
                            </Grid>


                            {/* SECTION ISSterilizied */}
                            <Grid item xs={12} sm={6}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="isSterilizied">
                                        <FormattedMessage id="Sterilizied" defaultMessage="Sterilizied"/></InputLabel>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                id="isSterilizied"
                                                checked={formik.values.isSterilizied}
                                                onChange={(e) => formik.setFieldValue('isSterilizied', e.target.checked)}
                                                name="isSterilizied"
                                                color="primary"
                                            />
                                        }
                                        label={intl.formatMessage({id: "isSterilizied"})}
                                    />
                                    {formik.touched.isSterilizied && formik.errors.isSterilizied && (
                                        <span style={{color: 'red'}}>{formik.errors.isSterilizied}</span>
                                    )}

                                    {/* SECTION SterilizieDate*/}
                                    {formik.values.isSterilizied && (

                                        <DatePicker
                                            label="Data di sterilizzazione"
                                            value={formik.values.sterilizationDate}
                                            onChange={(newDate) => {
                                                if (newDate) {
                                                    formik.setFieldValue('sterilizationDate', newDate)
                                                }
                                            }}
                                            format={"DD/MM/YYYY"}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error: Boolean(formik.touched.sterilizationDate && formik.errors.sterilizationDate),
                                                    helperText: formik.touched.sterilizationDate && formik.errors.sterilizationDate ? formik.errors.sterilizationDate : '',
                                                },
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Grid>

                            {/* SECTION RACE*/}
                            <Grid item xs={12} sm={4}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="race">
                                        <FormattedMessage id="Race" defaultMessage="Race"/>
                                    </InputLabel>
                                    <TextField
                                        fullWidth
                                        id="race"
                                        placeholder={intl.formatMessage({id: "Race"})}
                                        {...formik.getFieldProps('race')}
                                        error={Boolean(formik.touched.race && formik.errors.race)}
                                        helperText={formik.touched.race && formik.errors.race}
                                    />
                                </Stack>
                            </Grid>

                            {/* SECTION DIED DATE*/}
                            <Grid item xs={12} sm={4}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="diedDate">
                                        <FormattedMessage id="Died Date" defaultMessage="Died Date"/>
                                    </InputLabel>

                                    <DatePicker
                                        label={<FormattedMessage id="Insert in case of dog death"
                                                                 defaultMessage="Insert in case of dog death"/>}
                                        value={formik.values.diedDate}
                                        onChange={(newDate) => {
                                            if (newDate) {
                                                formik.setFieldValue('diedDate', newDate)
                                            }
                                        }}
                                        format={"DD/MM/YYYY"}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: Boolean(formik.touched.diedDate && formik.errors.diedDate),
                                                helperText: formik.touched.diedDate && formik.errors.diedDate ? formik.errors.diedDate : '',
                                            },
                                        }}
                                    />
                                </Stack>
                            </Grid>

                            {/* SECTION RETURNED DATE*/}
                            <Grid item xs={12} sm={4}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="returnedDate">
                                        <FormattedMessage id="Returned Date" defaultMessage="Returned Date"/>
                                    </InputLabel>

                                    <DatePicker
                                        label={<FormattedMessage id="Insert in case of dog retuned"
                                                                 defaultMessage="Insert in case of dog death"/>}
                                        value={formik.values.returnedDate}
                                        onChange={(newDate) => {
                                            if (newDate) {
                                                formik.setFieldValue('returnedDate', newDate)
                                            }
                                        }}
                                        format={"DD/MM/YYYY"}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: Boolean(formik.touched.returnedDate && formik.errors.returnedDate),
                                                helperText: formik.touched.returnedDate && formik.errors.returnedDate ? formik.errors.returnedDate : '',
                                            },
                                        }}
                                    />
                                </Stack>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <Divider/>
                    <DialogActions sx={{p: 2.5}}>
                        <Grid container justifyContent="flex-end" alignItems="center">
                            <Grid item>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Button color="error" onClick={closeModal}>
                                        <FormattedMessage id="Cancel" defaultMessage="Cancel"/>
                                    </Button>
                                    <Button type="submit" variant="contained">
                                        <FormattedMessage id="Save" defaultMessage="Save"/>
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </>
    )
}